import React, { useEffect } from 'react';
import TreeView from '@mui/lab/TreeView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TreeItem from '@mui/lab/TreeItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import { styled } from '@mui/material';
import { Checkbox } from 'ui';

export interface TreeBranch {
  id: string;
  name: string | null;
  children?: TreeBranch[];
  isRoot?: boolean;
}

export type Leaf = {
  id: string;
  parentId: string | null;
  name: string;
  isRoot?: boolean;
};

export const ROOT_ID = 'ROOT_ID';

const StyledTreeItem = styled(TreeItem)<{ invisible: 'invisible' | 'false' }>(({ invisible }) => ({
  '& > div.MuiTreeItem-content': {
    display: invisible === 'invisible' ? 'none' : 'flex'
  },
  '& > ul': {
    marginLeft: invisible === 'invisible' && '0px'
  }
}));

export function buildRecursiveTreeData(initialData: Leaf[]) {
  function tree(data: Leaf[], pid: string | null) {
    return data.reduce((accumulator, { id, parentId, ...other }) => {
      if (pid === parentId) {
        const item: TreeBranch = { id: String(id), ...other };
        const children = tree(data, String(id));
        if (children.length) item.children = children;
        accumulator.push(item);
      }
      return accumulator;
    }, [] as TreeBranch[]);
  }

  return tree(initialData, null);
}

interface Props {
  data: TreeBranch;
  onChange: (ids: string[]) => void;
  selectedIds: string[];
  hasIndeterminateMode?: boolean;
  filterQuery: string;
  defaultExpanded: string[];
  isAllSelected: boolean;
  shouldIncludeIndeterminate?: boolean;
  shouldExpandSelectedItemBranch?: boolean;
  isReadOnly?: boolean;
}

export default function Tree({
  data,
  onChange,
  selectedIds = [],
  hasIndeterminateMode = false,
  filterQuery = '',
  defaultExpanded = [],
  isAllSelected = false,
  shouldIncludeIndeterminate = false,
  shouldExpandSelectedItemBranch = false,
  isReadOnly = false
}: Props) {
  const [selected, setSelected] = React.useState<string[]>(selectedIds);
  const [indeterminateItems, setIndeterminateItems] = React.useState<string[]>([]);
  const [expanded, setExpanded] = React.useState<string[]>([ROOT_ID, ...defaultExpanded]);
  const [parentMap, setParentsMap] = React.useState<Record<string, string[]> | null>();
  const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };
  useEffect(() => {
    setParentsMap(goThroughAllNodes(data));
  }, [data]);

  useEffect(() => {
    if (filterQuery !== '') {
      const expandedIds = [ROOT_ID, data.id, ...getAllChildren(data)];
      setExpanded(expandedIds);
    } else {
      const expandedItemsList: string[] = [];

      // collect all parent items that have child node selected by default, add those parent items to an expanded
      // items list
      if (shouldExpandSelectedItemBranch) {
        selected.forEach(selectedItemId => {
          parentMap &&
            Object.keys(parentMap).forEach(key => {
              if (parentMap?.[key].includes(selectedItemId)) {
                expandedItemsList.push(key);
              }
            });
        });
      }
      setExpanded([ROOT_ID, ...defaultExpanded, ...expandedItemsList]);
    }
  }, [defaultExpanded, filterQuery, parentMap, shouldExpandSelectedItemBranch, selected]);

  useEffect(() => {
    const indeterminateItemsList = shouldIncludeIndeterminate ? indeterminateItems : [];
    if (selected.length === 0) {
      onChange([]);
    } else {
      // remove ROOT item and remove all duplicates
      onChange(
        [...selected, ...indeterminateItemsList].filter(
          (value, index, array) => value !== ROOT_ID && array.indexOf(value) === index
        )
      );
    }
  }, [selected, indeterminateItems]);

  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  function goThroughAllNodes(nodes: TreeBranch, map: Record<string, any> = {}) {
    if (!nodes.children) {
      return null;
    }

    map[nodes.id] = getAllChildren(nodes).splice(1);

    for (let childNode of nodes.children) {
      goThroughAllNodes(childNode, map);
    }

    return map;
  }

  // Get all children from the current node.
  function getAllChildren(childNode: TreeBranch | null, collectedNodes: any[] = []) {
    if (childNode === null) return collectedNodes;

    collectedNodes.push(childNode.id);

    if (Array.isArray(childNode.children)) {
      for (const node of childNode.children) {
        getAllChildren(node, collectedNodes);
      }
    }

    return collectedNodes;
  }

  // looks over the tree using Deep First Search for a node with given id
  // returns list of children nodes needs to be toggled (if found node has children)
  // and array of parent node ids as 'path'
  const getChildById = (nodes: TreeBranch, id: string) => {
    // array of all parent node ids from current node up to the root node
    const path: string[] = [];

    // recursive DFS
    function getNodeById(node: TreeBranch, id: string): TreeBranch | null {
      let result = null;

      if (node.id === id) {
        return node;
      } else if (Array.isArray(node.children)) {
        for (const childNode of node.children) {
          result = getNodeById(childNode, id);
          if (!!result) {
            path.push(node.id);
            return result;
          }
        }

        return result;
      }

      return result;
    }

    const nodeToToggle = getNodeById(nodes, id);

    return { childrenNodes: getAllChildren(nodeToToggle), path };
  };

  function getOnChange(checked: boolean, nodes: TreeBranch) {
    const { childrenNodes, path } = getChildById(data, nodes.id);

    let array = checked
      ? [...selected, ...childrenNodes]
      : selected.filter(value => !childrenNodes.includes(value)).filter(value => !path.includes(String(value)));

    setSelected(array);

    // iterate over all current and previous indeterminate items list
    // take children for every node
    // filter out those nodes which do not have at least one child checked.
    setIndeterminateItems(prevList => {
      const updatedList = [...prevList, ...path].filter(nodeId => {
        const { childrenNodes } = getChildById(data, nodeId);
        return childrenNodes.some(selectedId => array.includes(selectedId));
      });
      return updatedList.filter((value, index, array) => array.indexOf(value) === index);
    });
  }

  const renderTree = (nodes: TreeBranch) => {
    const allSelectedChildren =
      parentMap && parentMap[nodes.id]?.every((childNodeId: string) => selectedSet.has(childNodeId));
    const checked = selectedSet.has(nodes.id) || allSelectedChildren || false;

    const indeterminate =
      (hasIndeterminateMode &&
        parentMap &&
        parentMap[nodes.id]?.some((childNodeId: string) => selectedSet.has(childNodeId))) ||
      false;

    if (allSelectedChildren && !selectedSet.has(nodes.id)) {
      setSelected([...selected, nodes.id]);
    }

    let isInvisible: 'invisible' | 'false' = 'false';
    if (filterQuery !== '') {
      // show node if current node name includes query OR if any child of node includes query
      if (nodes.name?.toLowerCase().includes(filterQuery.toLowerCase()) && nodes.name !== 'root') {
        isInvisible = 'false';
      } else if (getAllChildren(nodes).some(child => child.name?.toLowerCase().includes(filterQuery.toLowerCase()))) {
        isInvisible = 'false';
      } else {
        isInvisible = 'invisible';
      }
    } else if (nodes.id === ROOT_ID || nodes.isRoot || nodes.name === 'root') {
      isInvisible = 'invisible';
    }

    return (
      <StyledTreeItem
        key={nodes.id}
        nodeId={String(nodes.id)}
        invisible={isInvisible}
        label={
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllSelected || checked}
                indeterminate={!isAllSelected && !checked && indeterminate}
                disabled={isAllSelected || isReadOnly}
                onChange={(event: any) => getOnChange(event.currentTarget.checked, nodes)}
                onClick={(e: any) => e.stopPropagation()}
              />
            }
            label={<>{nodes.name}</>}
            key={nodes.id}
          />
        }
      >
        {Array.isArray(nodes.children) ? nodes.children.map(node => renderTree(node)) : null}
      </StyledTreeItem>
    );
  };

  return (
    <TreeView
      defaultCollapseIcon={<ExpandMoreIcon />}
      expanded={expanded}
      defaultExpandIcon={<ChevronRightIcon />}
      onNodeToggle={handleToggle}
      disableSelection={isReadOnly}
    >
      {renderTree(data)}
    </TreeView>
  );
}
